/* Keyboard shortcuts.
 *
 * A planning app is a thing people are in every day, and every-day tools earn
 * their keyboard. This is the difference between an app you click through and
 * one you drive.
 *
 * The rules that keep shortcuts from becoming a hazard:
 *
 *   · **Never while typing.** A bare letter that does something while the
 *     cursor is in a text field is not a shortcut, it is a trap. Anything
 *     editable — input, textarea, contenteditable, select — swallows the key.
 *
 *   · **Never steal a browser or OS combination.** Ctrl/Cmd/Alt chords belong
 *     to the platform, so bare letters are used instead, with two deliberate
 *     exceptions people expect from applications: Escape closes, and `?`
 *     explains.
 *
 *   · **Discoverable.** A shortcut nobody knows about is dead code, so `?`
 *     lists them and the registry below is what that list is generated from —
 *     the help cannot drift from the behaviour because it is the same array.
 */

import { useEffect } from 'react';

export interface Shortcut {
  /** A single lowercase key, or 'Escape' / '?' . */
  key: string;
  label: string;
  /** Which screen it belongs to, for grouping in the help. */
  group: string;
  run: () => void;
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Bind a set of shortcuts for as long as the component is mounted.
 *
 * `enabled` exists so a screen can hand its keys back while a dialog is open —
 * two layers both answering the same keystroke is how a shortcut ends up doing
 * something surprising.
 */
export function useShortcuts(shortcuts: Shortcut[], enabled = true): void {
  useEffect(() => {
    if (!enabled) return;

    const onKey = (e: KeyboardEvent) => {
      /* A modifier means the user is talking to the browser, not to us —
         except Shift, which is how `?` is typed on most layouts. */
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTyping(e.target) && e.key !== 'Escape') return;

      const hit = shortcuts.find(s =>
        s.key === e.key || s.key === e.key.toLowerCase());
      if (!hit) return;

      e.preventDefault();
      hit.run();
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcuts, enabled]);
}
